import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, PhoneCall, KanbanSquare, Building2, FileText, FolderKanban, ListChecks, Receipt, Wallet,
  CreditCard, BarChart3, Trophy, UserCog, ScrollText, Settings, Search, Bell, LogOut, ChevronsLeft, Menu, X, CalendarClock, IdCard, Bot,
} from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../lib/api";
import { fdatetime } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import Logo from "./Logo";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Field } from "./common";

export const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: ["owner", "sales", "designer"], end: true },
  { section: "Sales", roles: ["owner", "sales"] },
  { to: "/leads", label: "Leads", icon: Users, roles: ["owner", "sales"] },
  { to: "/followups", label: "Follow-ups", icon: PhoneCall, roles: ["owner", "sales"] },
  { to: "/pipeline", label: "Pipeline", icon: KanbanSquare, roles: ["owner", "sales"] },
  { to: "/clients", label: "Clients", icon: Building2, roles: ["owner", "sales"] },
  { to: "/quotations", label: "Quotations", icon: FileText, roles: ["owner", "sales"] },
  { to: "/lead-agent", label: "Lead Agent", icon: Bot, roles: ["owner"] },
  { section: "Production", roles: ["owner", "designer", "sales"] },
  { to: "/projects", label: "Projects", icon: FolderKanban, roles: ["owner", "designer", "sales"] },
  { to: "/tasks", label: "Tasks", icon: ListChecks, roles: ["owner", "designer"] },
  { to: "/deadlines", label: "Deadlines", icon: CalendarClock, roles: ["owner", "designer", "sales"], flag: "sales_view_delivery" },
  { section: "Finance", roles: ["owner"] },
  { to: "/invoices", label: "Invoices", icon: Receipt, roles: ["owner"] },
  { to: "/payments", label: "Payments", icon: CreditCard, roles: ["owner"] },
  { to: "/expenses", label: "Expenses", icon: Wallet, roles: ["owner"] },
  { to: "/reports", label: "Reports", icon: BarChart3, roles: ["owner"] },
  { section: "Team", roles: ["owner", "sales"] },
  { to: "/performance", label: "Performance", icon: Trophy, roles: ["owner", "sales"] },
  { to: "/hr", label: "HR & Payroll", icon: IdCard, roles: ["owner"] },
  { to: "/users", label: "Users", icon: UserCog, roles: ["owner"] },
  { to: "/audit", label: "Audit Log", icon: ScrollText, roles: ["owner"] },
  { to: "/settings", label: "Settings", icon: Settings, roles: ["owner"] },
];

function Sidebar({ collapsed, role, onNavigate }) {
  const { settings } = useAuth();
  return (
    <nav className="flex-1 overflow-y-auto no-scrollbar px-3 py-2 space-y-0.5">
      {NAV.filter((n) => n.roles.includes(role) && !(role === "sales" && n.flag && !settings?.[n.flag])).map((n) =>
        n.section ? (
          !collapsed && <div key={n.section} className="px-3 pt-5 pb-1.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">{n.section}</div>
        ) : (
          <NavLink key={n.to} to={n.to} end={n.end} onClick={onNavigate} data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
            title={n.label}
            className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
              isActive ? "vmw-gradient text-white shadow-[0_6px_20px_-8px_rgba(123,47,255,0.8)]" : "text-slate-300 hover:bg-white/5 hover:text-white"}`}>
            <n.icon className="h-[18px] w-[18px] shrink-0" />
            {!collapsed && <span className="truncate">{n.label}</span>}
          </NavLink>
        )
      )}
    </nav>
  );
}

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  useEffect(() => {
    if (q.trim().length < 2) { setRes([]); return; }
    const t = setTimeout(() => api.get(`/search?q=${encodeURIComponent(q)}`).then((r) => { setRes(r.data.results); setOpen(true); }).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="relative flex-1 max-w-md">
      <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
      <input data-testid="global-search-input" value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => res.length && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)} placeholder="Search leads, clients, projects, invoices…"
        className="vmw-input pl-9 bg-muted/50 border-transparent" />
      {open && (
        <div className="absolute z-50 mt-2 w-full vmw-card shadow-xl max-h-96 overflow-y-auto" data-testid="global-search-results">
          {res.length === 0 ? <div className="p-4 text-sm text-muted-foreground">No matching records you can access</div> : res.map((r, i) => (
            <button key={`${r.type}-${r.link}`} data-testid={`search-result-${i}`} onMouseDown={() => { nav(r.link); setQ(""); setOpen(false); }}
              className="w-full text-left px-4 py-2.5 hover:bg-violet-50 flex items-center gap-3 border-b last:border-0">
              <span className="text-[10px] font-bold uppercase tracking-wider text-violet-600 w-14 shrink-0">{r.type}</span>
              <span className="min-w-0"><span className="block text-sm font-medium truncate">{r.title}</span>
                <span className="block text-xs text-muted-foreground truncate">{r.subtitle}</span></span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Notifications() {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const ref = useRef();
  const nav = useNavigate();
  const load = () => api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const click = async (n) => { await api.post(`/notifications/${n.id}/read`); setOpen(false); load(); if (n.link) nav(n.link); };
  return (
    <div className="relative" ref={ref}>
      <button data-testid="notifications-button" onClick={() => setOpen(!open)} className="relative h-9 w-9 grid place-items-center rounded-lg hover:bg-muted">
        <Bell className="h-5 w-5 text-slate-600" />
        {data.unread > 0 && <span data-testid="notifications-unread-count" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full vmw-gradient text-white text-[10px] font-bold grid place-items-center">{data.unread}</span>}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-[340px] max-w-[90vw] vmw-card shadow-xl z-50" data-testid="notifications-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <span className="font-display font-semibold">Notifications</span>
            <button data-testid="notifications-read-all" className="text-xs text-primary font-semibold" onClick={async () => { await api.post("/notifications/read-all"); load(); }}>Mark all read</button>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {data.items.length === 0 && <div className="p-6 text-sm text-center text-muted-foreground">You're all caught up</div>}
            {data.items.map((n) => (
              <button key={n.id} onClick={() => click(n)} className={`w-full text-left px-4 py-3 border-b last:border-0 hover:bg-violet-50 ${n.read ? "" : "bg-violet-50/60"}`}>
                <div className="flex items-center gap-2">{!n.read && <span className="h-2 w-2 rounded-full bg-primary shrink-0" />}<span className="text-sm font-semibold">{n.title}</span></div>
                <div className="text-xs text-muted-foreground mt-0.5">{n.message}</div>
                <div className="text-[10px] text-slate-400 mt-1">{fdatetime(n.created_at)}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ForcePasswordChange() {
  const { user, setUser } = useAuth();
  const [f, setF] = useState({ current_password: "", new_password: "" });
  const save = async () => {
    try { await api.post("/auth/change-password", f); toast.success("Password updated"); setUser({ ...user, must_change_password: false }); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!user?.must_change_password}>
      <DialogContent className="sm:max-w-md" onInteractOutside={(e) => e.preventDefault()} data-testid="force-password-dialog">
        <DialogHeader><DialogTitle>Set your own password</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Your account was created or reset by the owner. Please choose a new password to continue.</p>
        <Field label="Temporary password"><input type="password" data-testid="force-pw-current" className="vmw-input" value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} /></Field>
        <Field label="New password (min 8 characters)"><input type="password" data-testid="force-pw-new" className="vmw-input" value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} /></Field>
        <Button data-testid="force-pw-save" className="vmw-gradient" onClick={save}>Update password</Button>
      </DialogContent>
    </Dialog>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [mobile, setMobile] = useState(false);
  const nav = useNavigate();
  return (
    <div className="min-h-screen flex bg-background">
      <aside className={`hidden lg:flex flex-col vmw-sidebar sticky top-0 h-screen transition-[width] duration-200 ${collapsed ? "w-[76px]" : "w-[248px]"}`} data-testid="sidebar">
        <div className="h-16 flex items-center justify-between px-4 border-b border-white/5">
          <Logo dark collapsed={collapsed} />
          {!collapsed && <button data-testid="sidebar-collapse" onClick={() => setCollapsed(true)} className="text-slate-400 hover:text-white"><ChevronsLeft className="h-4 w-4" /></button>}
        </div>
        {collapsed && <button data-testid="sidebar-expand" onClick={() => setCollapsed(false)} className="mx-auto mt-2 text-slate-400 hover:text-white"><Menu className="h-4 w-4" /></button>}
        <Sidebar collapsed={collapsed} role={user.role} />
        {!collapsed && <div className="p-4 text-[10px] text-slate-500 border-t border-white/5">Vayora Media Works CRM · IST</div>}
      </aside>
      {mobile && (
        <div className="lg:hidden fixed inset-0 z-50 flex" data-testid="mobile-sidebar">
          <div className="w-[260px] vmw-sidebar flex flex-col h-full">
            <div className="h-16 flex items-center justify-between px-4"><Logo dark /><button onClick={() => setMobile(false)} className="text-slate-300" data-testid="mobile-sidebar-close"><X className="h-5 w-5" /></button></div>
            <Sidebar role={user.role} onNavigate={() => setMobile(false)} />
          </div>
          <div className="flex-1 bg-black/40" onClick={() => setMobile(false)} />
        </div>
      )}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b flex items-center gap-3 px-4 sm:px-6">
          <button className="lg:hidden" onClick={() => setMobile(true)} data-testid="mobile-menu-button"><Menu className="h-5 w-5" /></button>
          <GlobalSearch />
          <div className="ml-auto flex items-center gap-2">
            <Notifications />
            <div className="hidden sm:flex items-center gap-2.5 pl-3 ml-1 border-l" data-testid="profile-info">
              <div className="h-9 w-9 rounded-full vmw-gradient text-white grid place-items-center font-bold text-sm">{user.name?.[0]}</div>
              <div className="leading-tight"><div className="text-sm font-semibold truncate max-w-[140px]">{user.name}</div><div className="text-[11px] text-muted-foreground capitalize">{user.role}</div></div>
            </div>
            <button data-testid="logout-button" title="Logout" onClick={async () => { await logout(); nav("/login"); }} className="h-9 w-9 grid place-items-center rounded-lg hover:bg-rose-50 text-slate-500 hover:text-rose-600"><LogOut className="h-[18px] w-[18px]" /></button>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1600px] w-full mx-auto"><Outlet /></main>
      </div>
      <ForcePasswordChange />
    </div>
  );
}
